import { useEffect, useState } from 'react'
import { api } from '../api/client'

interface VorlageAntwort {
  id: number
  art: string
  bezeichnung: string
  betreff: string | null
  inhaltHtml: string
  platzhalterHinweis: string | null
  aktiv: boolean
}

interface Vorschau {
  betreff: string | null
  html: string
}

const ART_TEXT: Record<string, string> = {
  BASIS: 'Basislayout',
  EINLADUNG: 'Einladung',
  PASSWORT_ZURUECKSETZEN: 'Passwort zurücksetzen',
  FRISTENBERICHT: 'Fristenbericht',
  DIENSTBERICHT: 'Dienstbericht',
}

export function Mailvorlagen() {
  const [vorlagen, setVorlagen] = useState<VorlageAntwort[]>([])
  const [gewaehlt, setGewaehlt] = useState<VorlageAntwort | null>(null)
  const [vorschau, setVorschau] = useState<Vorschau | null>(null)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function laden() {
    try {
      const daten = await api.get<VorlageAntwort[]>('/api/plattform/mailvorlagen')
      setVorlagen(daten)
      setGewaehlt((alt) => (alt ? (daten.find((v) => v.id === alt.id) ?? daten[0]) : daten[0]))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function speichern() {
    if (!gewaehlt) return
    setLaeuft(true)
    setMeldung(null)
    try {
      await api.put(`/api/plattform/mailvorlagen/${gewaehlt.id}`, {
        bezeichnung: gewaehlt.bezeichnung,
        betreff: gewaehlt.betreff,
        inhaltHtml: gewaehlt.inhaltHtml,
        aktiv: gewaehlt.aktiv,
      })
      setMeldung({ art: 'erfolg', text: 'Vorlage gespeichert' })
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function zeigeVorschau() {
    if (!gewaehlt) return
    setLaeuft(true)
    try {
      setVorschau(await api.post<Vorschau>(`/api/plattform/mailvorlagen/${gewaehlt.id}/vorschau`))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Vorschau fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  const istBasis = gewaehlt?.art === 'BASIS'
  const basisOhnePlatzhalter = istBasis && !gewaehlt.inhaltHtml.includes('[MESSAGE]')

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Mail-Vorlagen</h1>
          <p>
            Das Basislayout enthält den Block <code>[MESSAGE]</code>. Dort setzen alle anderen
            Vorlagen ihren Inhalt ein — eine Änderung am Layout wirkt auf alle Mails.
          </p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      <div className="werkzeugleiste">
        {vorlagen.map((v) => (
          <button
            key={v.id}
            className={gewaehlt?.id === v.id ? 'knopf-primaer' : 'knopf-schlicht'}
            onClick={() => {
              setGewaehlt(v)
              setVorschau(null)
            }}
          >
            {ART_TEXT[v.art] ?? v.art}
            {!v.aktiv && ' (aus)'}
          </button>
        ))}
      </div>

      {gewaehlt && (
        <div className="karte abschnitt">
          <h2>{gewaehlt.bezeichnung}</h2>

          {gewaehlt.platzhalterHinweis && (
            <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem', marginTop: 8 }}>
              Verfügbare Platzhalter: <code>{gewaehlt.platzhalterHinweis}</code>
            </p>
          )}

          {basisOhnePlatzhalter && (
            <div className="meldung fehler">
              Das Basislayout muss <code>[MESSAGE]</code> enthalten — sonst hätte keine Mail mehr
              einen Inhalt. Das Speichern wird abgelehnt.
            </div>
          )}

          {!istBasis && (
            <div className="feld">
              <label htmlFor="betreff">Betreff</label>
              <input
                id="betreff"
                type="text"
                value={gewaehlt.betreff ?? ''}
                onChange={(e) => setGewaehlt({ ...gewaehlt, betreff: e.target.value })}
              />
            </div>
          )}

          <div className="feld">
            <label htmlFor="inhalt">HTML-Inhalt</label>
            <textarea
              id="inhalt"
              rows={18}
              spellCheck={false}
              style={{
                width: '100%',
                padding: '11px 13px',
                border: '1px solid var(--grau-300)',
                borderRadius: 'var(--radius-klein)',
                fontFamily: 'var(--schrift-mono)',
                fontSize: '0.8rem',
                lineHeight: 1.5,
              }}
              value={gewaehlt.inhaltHtml}
              onChange={(e) => setGewaehlt({ ...gewaehlt, inhaltHtml: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="knopf-primaer"
              disabled={laeuft || basisOhnePlatzhalter}
              onClick={() => void speichern()}
            >
              Speichern
            </button>
            <button className="knopf-schlicht" disabled={laeuft} onClick={() => void zeigeVorschau()}>
              Vorschau
            </button>
            {!istBasis && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 400, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={gewaehlt.aktiv}
                  onChange={(e) => setGewaehlt({ ...gewaehlt, aktiv: e.target.checked })}
                />
                Vorlage aktiv
              </label>
            )}
          </div>
        </div>
      )}

      {vorschau && (
        <div className="karte">
          <h2>Vorschau</h2>
          {vorschau.betreff && (
            <p style={{ color: 'var(--grau-700)', fontSize: '0.9rem' }}>
              <strong>Betreff:</strong> {vorschau.betreff}
            </p>
          )}
          {/* srcDoc statt innerHTML: Die Vorlage läuft in einem eigenen Dokument und
              kann weder Stile noch Skripte der Anwendung beeinflussen. */}
          <iframe
            title="Mailvorschau"
            srcDoc={vorschau.html}
            sandbox=""
            style={{
              width: '100%',
              height: 720,
              border: '1px solid var(--grau-200)',
              borderRadius: 'var(--radius-klein)',
              background: '#fff',
            }}
          />
        </div>
      )}
    </>
  )
}
